/**
 * The `file` field type, finally collectible — Step 73.
 *
 * Uploads happen IMMEDIATELY and the field's value is a list of references,
 * not bytes. Two reasons that matters: a half-filled form never carries
 * megabytes of state, and the server can validate the answer like any other
 * — a file field is then just an array of ids, and `writeValues` needs no
 * special case.
 *
 * Uploads land as `pending`, owned by the uploader and readable by nobody
 * else, until the submission claims them for the ticket. So an abandoned
 * form leaks nothing.
 *
 * Surface-aware by necessity: the portal must post to its own door
 * (`/api/portal/media`), never the agent one — three doors, never crossed.
 */
import { useRef, useState } from 'react';
import { Eye, FileText, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import type { FileFieldValue } from '@itsm/shared';
import { AttachmentViewer, canPreview } from '../attachments/AttachmentViewer';
import { Modal } from '../../components/ui/modal';
import { cn } from '../../lib/utils';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FileFieldInput({
  value,
  onChange,
  accept,
  maxFiles = 1,
  maxSizeMb,
  surface = 'agent',
}: {
  value: FileFieldValue[];
  onChange: (next: FileFieldValue[]) => void;
  accept?: string | undefined;
  maxFiles?: number;
  maxSizeMb?: number | undefined;
  surface?: 'agent' | 'portal';
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<FileFieldValue | null>(null);
  /**
   * Step 112 — drag and drop onto the control itself.
   *
   * A counter rather than a boolean, and it is not fussiness: `dragleave`
   * fires every time the pointer crosses into a CHILD element, so a boolean
   * makes the highlight flicker as you move over the icon or the label. The
   * counter only reaches zero when the pointer has genuinely left.
   */
  const [dragDepth, setDragDepth] = useState(0);
  const files = Array.isArray(value) ? value : [];
  const full = files.length >= maxFiles;

  async function add(picked: FileList) {
    setError(null);
    const room = maxFiles - files.length;
    if (room <= 0) return;
    const next: FileFieldValue[] = [];
    setBusy(true);
    for (const file of Array.from(picked).slice(0, room)) {
      // Check the size HERE as well as on the server: telling someone their
      // 40 MB file is too big after they have waited for it to upload is the
      // rude way round.
      if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
        setError(`${file.name} is larger than ${maxSizeMb} MB.`);
        continue;
      }
      const form = new FormData();
      form.append('file', file);
      // Evidence, not artwork: the `attachment` purpose accepts documents as
      // well as images and never re-encodes what it stores.
      form.append('purpose', 'attachment');
      // Bare fetch on purpose: apiSend/portalSend serialise JSON, and a
      // multipart body must reach the network untouched so the browser sets
      // its own boundary. The endpoint differs per surface so the portal
      // never calls an agent-domain route.
      const res = await fetch(surface === 'portal' ? '/api/portal/media' : '/api/media', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setError(body?.error ?? `Could not upload ${file.name}.`);
        continue;
      }
      next.push({
        id: body.id,
        filename: body.filename,
        contentType: body.contentType,
        size: body.size,
      });
    }
    setBusy(false);
    if (next.length) onChange([...files, ...next]);
  }

  return (
    <div className="space-y-2">
      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2 rounded-md border bg-muted/20 px-2.5 py-1.5 text-sm"
            >
              {f.contentType?.startsWith('image/') ? (
                <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              {canPreview(f.contentType) ? (
                // Preview in the page rather than navigating to the file —
                // navigation is precisely what hands a PDF to the browser's
                // own viewer, which is the risk we are avoiding.
                <button
                  type="button"
                  onClick={() => setPreviewing(f)}
                  className="min-w-0 flex-1 truncate text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {f.filename}
                </button>
              ) : (
                <span className="min-w-0 flex-1 truncate">{f.filename}</span>
              )}
              <span className="shrink-0 text-xs text-muted-foreground">{humanSize(f.size)}</span>
              {canPreview(f.contentType) && (
                <button
                  type="button"
                  onClick={() => setPreviewing(f)}
                  aria-label={`Preview ${f.filename}`}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onChange(files.filter((x) => x.id !== f.id))}
                aria-label={`Remove ${f.filename}`}
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!full && (
        <button
          type="button"
          disabled={busy}
          onClick={() => input.current?.click()}
          // Drop anywhere on the control. `preventDefault` on dragOver is what
          // makes this a drop target at all — without it the browser navigates
          // away to open the file, losing whatever was being typed.
          onDragEnter={(e) => {
            e.preventDefault();
            setDragDepth((n) => n + 1);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragDepth((n) => Math.max(0, n - 1))}
          onDrop={(e) => {
            e.preventDefault();
            setDragDepth(0);
            if (e.dataTransfer.files?.length) void add(e.dataTransfer.files);
          }}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-3 text-sm',
            'text-muted-foreground hover:border-brand hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
            dragDepth > 0 && 'border-brand bg-brand/5 text-foreground',
          )}
        >
          <Paperclip className={cn('h-4 w-4', busy && 'animate-pulse')} />
          {busy
            ? 'Uploading…'
            : dragDepth > 0
              ? 'Drop to attach'
              : maxFiles > 1
                ? 'Attach files — or drop them here'
                : 'Attach a file — or drop it here'}
        </button>
      )}

      <p className="text-xs text-muted-foreground">
        {maxFiles > 1 ? `Up to ${maxFiles} files` : 'One file'}
        {maxSizeMb ? `, ${maxSizeMb} MB each` : ''}
        {accept ? ` · ${accept}` : ''}
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}

      <Modal
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        title={previewing?.filename ?? ''}
      >
        <div className="p-4">
          {previewing && (
            <AttachmentViewer
              id={previewing.id}
              filename={previewing.filename}
              contentType={previewing.contentType}
            />
          )}
        </div>
      </Modal>

      <input
        ref={input}
        type="file"
        {...(accept ? { accept } : {})}
        multiple={maxFiles > 1}
        className="hidden"
        onChange={(e) => {
          const picked = e.target.files;
          // Reset first so re-picking the same file still fires a change.
          const el = e.target;
          if (picked?.length) void add(picked).finally(() => (el.value = ''));
          else el.value = '';
        }}
      />
    </div>
  );
}
